import { Navigate, useRoutes } from 'react-router-dom';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
//
import { mainRoutes } from './main';
import { publicJoinRoutes } from './public-join';
import { publicSurveyRoutes } from './public-survey';
import { publicUnsubscribeRoutes } from './public-unsubscribe';

import { useAuthContext } from 'src/auth/hooks';
import { SplashScreen } from 'src/components/loading-screen';
import { useOrganizationContext } from 'src/context/organization';
import { useTenantConfig } from 'src/context/tenant';
import { useIsAppOnlyUser } from 'src/hooks/use-app-only-user';
import { paths } from 'src/routes/paths';
import { adminToolsRoutes } from './adminTools';
import { assignedTasksRoutes } from './assigned-tasks';
import { authRoutes } from './auth';
import { billingRoutes } from './billing';
import { campaigndRoutes } from './campaign';
import { dashboardRoutes } from './dashboard';
import { automatedVoterReportRoutes } from './automated-voter-report';
import { downloadsRoutes } from './downloads';
import { financeRoutes } from './finances';
import { helpRoutes } from './help';
import { machineToMachineRoutes } from './machine-to-machine-api';
import { nationalRoutes } from './national';
import { questionBankRoutes } from './manager';
import { reportsRoutes } from './reports';
import { smsSurveyRoutes } from './smsSurveys';
import { superadminRoutes } from './superadmin';
import { teamRoutes } from './teams';
import { uploadsRoutes } from './uploads';
import { dataDirectorRoutes } from './data-director';
import { userRoutes } from './users';
import { volunteersRoutes } from './volunteers';
import { voterCommunicationsRoutes } from './voter-communications';
import { voterRoutes } from './voters';
import { VoicemailSetupRoutes } from './voicemail-setup';
import { NearMeSettingsRoutes } from './near-me-settings';

// ----------------------------------------------------------------------

/**
 * The default screen — where `/` and sign-in land — decided by the role in the *selected*
 * organization. It waits for the org context to settle: until then the effective role is the
 * global-role fallback, which says `user` for many managers and would send them to Assigned
 * Tasks, where nothing would move them on once their real role arrived.
 */
function HomeRedirect() {
  const { user } = useAuthContext();
  const { isLegislative } = useTenantConfig();
  const { isSelectionResolved, roleInSelectedOrganization, selectedOrganizationId } =
    useOrganizationContext();
  const isAppOnly = useIsAppOnlyUser();
  const globalRole = user?.users_user_roles?.[0]?.user_roles_roles?.code;

  // No org to wait for: the app-download page is the only place an app-only user belongs.
  if (isAppOnly) return <Navigate to={paths.volunteers.root} replace />;

  if (!isSelectionResolved) return <SplashScreen />;

  return (
    <Navigate
      to={PATH_AFTER_LOGIN(
        globalRole,
        roleInSelectedOrganization,
        isLegislative,
        selectedOrganizationId
      )}
      replace
    />
  );
}

export default function Router() {
  const { terminology } = useTenantConfig();

  return useRoutes([
    {
      path: '/',
      element: <HomeRedirect />,
    },

    // Auth routes
    ...authRoutes,

    // Dashboard routes
    ...dashboardRoutes,

    ...campaigndRoutes,

    // Public, unauthenticated: the survey link texted to a voter.
    ...publicSurveyRoutes,

    // Public, unauthenticated: an organization's volunteer invite link.
    ...publicJoinRoutes,

    // Public, unauthenticated: the unsubscribe link in a quick-message email.
    ...publicUnsubscribeRoutes,

    // Main routes
    ...mainRoutes,

    ...superadminRoutes,

    ...nationalRoutes,

    ...questionBankRoutes,

    ...smsSurveyRoutes,

    ...voterRoutes(terminology),
    ...reportsRoutes,
    ...teamRoutes,
    ...helpRoutes,
    ...userRoutes,
    ...financeRoutes,
    ...billingRoutes,
    ...adminToolsRoutes,
    ...voterCommunicationsRoutes,
    ...automatedVoterReportRoutes,
    ...downloadsRoutes,
    ...uploadsRoutes,
    ...dataDirectorRoutes,
    ...volunteersRoutes,
    ...assignedTasksRoutes,
    ...machineToMachineRoutes,
    ...VoicemailSetupRoutes,
    ...NearMeSettingsRoutes,
    // No match 404
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
